import { useState, useEffect, useRef } from 'react';

interface EasykitDayStatusBadgeProps {
  status: string | null;
  onStatusChange: (newStatus: string) => void;
}

export function EasykitDayStatusBadge({ status, onStatusChange }: EasykitDayStatusBadgeProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click outside handler
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Escape key handler — stop propagation so the global Escape in
  // EasykitPlanningView doesn't ALSO close the open detail panel / exit modes.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown, true); // capture phase
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open]);

  const currentNormalized = (!status || status === 'none' || status === 'OPEN') ? 'none' : status;

  const options = [
    { value: 'none', label: 'Beschikbaar', color: 'bg-green-400' },
    { value: 'LEV', label: 'Leveringen vol', color: 'bg-amber-400' },
    { value: '.', label: 'Vol', color: 'bg-red-400' },
  ];

  const indicatorColor = currentNormalized === 'LEV' ? 'bg-amber-400'
    : currentNormalized === '.' ? 'bg-red-400'
    : 'bg-green-400';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-4 h-4 rounded-full ${indicatorColor} cursor-pointer hover:opacity-80 transition-opacity`}
        aria-label="Dagstatus wijzigen"
        aria-haspopup="menu"
        aria-expanded={open}
      />
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-5 z-50 bg-white border border-gray-200 rounded shadow-lg py-1 min-w-[160px]"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="menuitemradio"
              aria-checked={currentNormalized === opt.value}
              onClick={() => { onStatusChange(opt.value); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 ${currentNormalized === opt.value ? 'bg-gray-100' : ''}`}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${opt.color}`} />
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
